import React, { useMemo, useState } from 'react';
import { FileText, Download, Share2, Printer, Calendar, Search, ChevronDown, X, Clock, FilePlus, MoreVertical, Sparkles, User, Users, LayoutDashboard } from 'lucide-react';
import { ROLES, getRole } from '../data/framework';
import { personById } from '../data/demoOrganisation';
import { CURRENT_CYCLE, REPORTS, type Report } from '../data/reports';
import { DOMAIN_SCORES } from '../data/scoring';
type TabId = 'overview' | 'current' | 'historical';
export function Reports() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('All Time');
  const [roleId, setRoleId] = useState('all');
  const [domain, setDomain] = useState('all');
  const isLibraryTab = activeTab === 'current' || activeTab === 'historical';
  const filterRole = getRole(roleId);
  const filteredReports = useMemo(() => {
    if (!isLibraryTab) return [];
    const q = searchQuery.trim().toLowerCase();
    return REPORTS.filter(report => {
      const isCurrent = report.cycle === CURRENT_CYCLE;
      if (activeTab === 'current' && !isCurrent) return false;
      if (activeTab === 'historical' && isCurrent) return false;
      if (q && !report.title.toLowerCase().includes(q)) return false;
      if (roleId !== 'all' && !report.roleIds.includes(roleId)) return false;
      if (domain !== 'all' && !report.domains.includes(domain)) return false;
      return true;
    });
  }, [activeTab, searchQuery, roleId, domain, isLibraryTab]);
  const currentCount = REPORTS.filter(r => r.cycle === CURRENT_CYCLE).length;
  const clearFilters = () => {
    setRoleId('all');
    setDomain('all');
    setSearchQuery('');
    setDateRange('All Time');
  };
  const activeFilterCount = (roleId !== 'all' ? 1 : 0) + (domain !== 'all' ? 1 : 0) + (dateRange !== 'All Time' ? 1 : 0);
  const tabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'current',
    label: 'Current Cycle'
  }, {
    id: 'historical',
    label: 'Historical Archive'
  }];
  const selectClass = 'w-full pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-slate-400';
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-1963" data-mp="rwpg4yharz0l">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-1964" data-mp="wvsddwr1d0y0">
        <div data-id="element-1965" data-mp="hysyroxro7u1">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1966" data-mp="hz1dznhd3p8e">
            Reports & Insights
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-1967" data-mp="mx2txzjhr8fn">
            Competency-domain results for board members and executives.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-1968" data-mp="2cdlpb9jsm8f4">
          <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center bg-white" data-id="element-1969" data-mp="2hcf73e644ll3">
            <Download className="h-4 w-4 mr-2" data-id="element-1970" data-mp="5m7a4c6h1eow" />
            Export All
          </button>
          <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-1971" data-mp="ne04tqe85th">
            <FilePlus className="h-4 w-4 mr-2" data-id="element-1972" data-mp="amlmg6f3vasq" />
            Generate Report
          </button>
        </div>
      </div>

      <div className="border-b border-slate-200" data-id="element-1973" data-mp="odgmct7mmhk6">
        <nav className="-mb-px flex space-x-8" aria-label="Report views" data-id="element-1974" data-mp="kdpzv1p4unlg">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} aria-current={isActive ? 'page' : undefined} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${isActive ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-1975" data-mp="yav20qlq72bf">
                {tab.id === 'overview' && <LayoutDashboard className="h-4 w-4 mr-2" data-id="element-1976" data-mp="31kgd93oonb6l" />}
                {tab.id === 'historical' && <Clock className="h-4 w-4 mr-2" data-id="element-1977" data-mp="36lvsug83cphl" />}
                {tab.label}
                {tab.id === 'current' && <span className="ml-2 py-0.5 px-2.5 rounded-full text-xs bg-slate-100 text-slate-600" data-id="element-1978" data-mp="3bj5s0nla8fim">
                    {currentCount}
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      {activeTab === 'overview' ? <OverviewPanel onBrowseReports={() => setActiveTab('current')} data-id="element-1979" data-mp="2nss150efompm" /> : <>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-id="element-1980" data-mp="2smmdfirplzeo">
            <div className="flex flex-col lg:flex-row gap-3" data-id="element-1981" data-mp="1v55vznpk5a0n">
              <div className="relative flex-1" data-id="element-1982" data-mp="2048ns6gj1jqo">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1983" data-mp="202xpun9rj67h" />
                <input type="text" placeholder="Search reports by name..." aria-label="Search reports" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all" data-id="element-1984" data-mp="h948ej98ny1y" />
              </div>
              <div className="relative lg:w-56" data-id="element-1985" data-mp="3po64mjffay81">
                <select value={roleId} onChange={e => {
              setRoleId(e.target.value);
              setDomain('all');
            }} aria-label="Role" className={selectClass} data-id="element-1986" data-mp="3lncgboy2jhem">
                  <option value="all" data-id="element-1987" data-mp="36qv836l6eiwa">All roles</option>
                  {ROLES.map(r => <option key={r.id} value={r.id} data-id="element-1988" data-mp="2x3915pppwjgz">
                      {r.name}
                    </option>)}
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1989" data-mp="323dkdni0fsaw" />
              </div>
              <div className="relative lg:w-64" data-id="element-1990" data-mp="2s3wgnbmbjohk">
                <select value={domain} onChange={e => setDomain(e.target.value)} aria-label="Competency domain" disabled={!filterRole} className={selectClass} data-id="element-1991" data-mp="2s4o0jp6jzxs0">
                  <option value="all" data-id="element-1992" data-mp="371oulzc7w4tn">
                    {filterRole ? 'All competency domains' : 'Choose a role for domains'}
                  </option>
                  {filterRole?.domains.map(d => <option key={d.id} value={d.name} data-id="element-1993" data-mp="2mp0g9fok03d">
                      {d.name}
                    </option>)}
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1994" data-mp="7fi8b66g3ojo" />
              </div>
              <div className="relative lg:w-44" data-id="element-1995" data-mp="1n5j33n6h4ml7">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1996" data-mp="1n4puegnyfn0f" />
                <select value={dateRange} onChange={e => setDateRange(e.target.value)} aria-label="Date range" className={`${selectClass} pl-9`} data-id="element-1997" data-mp="21tcjvb7apv5d">
                  <option value="All Time" data-id="element-1998" data-mp="146558u5mirpf">All Time</option>
                  <option value="Last 30 Days" data-id="element-1999" data-mp="147ol4jndb0qo">Last 30 Days</option>
                  <option value="This Year" data-id="element-2000" data-mp="1j3o44pc0mlal">This Year</option>
                  <option value="Last Year" data-id="element-2001" data-mp="1j11m1vri0ne4">Last Year</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-2002" data-mp="1e6e64lak91b5" />
              </div>
            </div>
            {activeFilterCount > 0 && <div className="flex justify-end" data-id="element-2003" data-mp="2piykm33bgifh">
                <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-900 flex items-center" data-id="element-2004" data-mp="mxba0se75doq">
                  <X className="h-3 w-3 mr-1" data-id="element-2005" data-mp="rtjhrkq2e4lv" />
                  Clear filters
                </button>
              </div>}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2006" data-mp="11mh6w9pkartg">
            {filteredReports.length === 0 ? <div className="p-12 text-center" data-id="element-2007" data-mp="16n4z2ypj1ep9">
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2008" data-mp="1alc9lbtedjs0">
                  <FileText className="h-6 w-6 text-slate-400" data-id="element-2009" data-mp="1fm01dzpga4na" />
                </div>
                <h3 className="text-sm font-medium text-slate-900 mb-1" data-id="element-2010" data-mp="1kj8ngbdpystt">
                  No reports found
                </h3>
                <p className="text-sm text-slate-500" data-id="element-2011" data-mp="3hey1mglk3zi9">
                  Try adjusting your search or filter criteria.
                </p>
                {activeFilterCount > 0 && <button onClick={clearFilters} className="mt-4 text-sm text-indigo-600 font-medium hover:text-indigo-800" data-id="element-2012" data-mp="3etypd3ic5bhg">
                    Clear all filters
                  </button>}
              </div> : <div className="divide-y divide-slate-100" data-id="element-2013" data-mp="305c98zkvwfp6">
                {filteredReports.map(report => <ReportRow key={report.id} report={report} data-id="element-2014" data-mp="1jsu2rxh3cuz" />)}
              </div>}
          </div>
        </>}
    </div>;
}
const OVERVIEW_PEOPLE = [{
  personId: 'p2',
  score: '4.4'
}, {
  personId: 'p3',
  score: '4.1'
}, {
  personId: 'p1',
  score: '4.6'
}];
function OverviewPanel({
  onBrowseReports
}: {
  onBrowseReports: () => void;
}) {
  const ceo = getRole('ceo');
  const aggregates = (ceo?.domains ?? []).slice(0, 3).map((d, i) => {
    const s = DOMAIN_SCORES[i];
    const score = (s.board + s.peers + s.directs) / 3;
    return {
      label: d.name,
      score
    };
  });
  const individualCount = REPORTS.filter(r => r.cycle === CURRENT_CYCLE && r.type === 'Individual').length;
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2015" data-mp="2qe1pm1u8g4u3">
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 shadow-sm col-span-1 md:col-span-2" data-id="element-2016" data-mp="2qgedevmo99f2">
        <div className="flex items-center mb-4" data-id="element-2017" data-mp="2llryiihdsyq8">
          <div className="bg-indigo-100 p-2 rounded-lg mr-3" data-id="element-2018" data-mp="2zdyiygrkhrvh">
            <Sparkles className="h-5 w-5 text-indigo-600" data-id="element-2019" data-mp="2vdobt0s1gvf1" />
          </div>
          <h2 className="text-lg font-semibold text-indigo-900" data-id="element-2020" data-mp="2vfh75jx7bt7q">
            Executive Summary
          </h2>
        </div>
        <div className="space-y-3 text-indigo-900/80 text-sm leading-relaxed" data-id="element-2021" data-mp="34ece4y82l7c3">
          <p data-id="element-2022" data-mp="34c9q2jqh9oa0">
            Across the <strong data-id="element-2023" data-mp="1eywkysajjw7u">{CURRENT_CYCLE}</strong>, executives score
            highest in <strong data-id="element-2024" data-mp="1f0zh7u2aell0">{aggregates[2]?.label}</strong> and lowest in{' '}
            <strong data-id="element-2025" data-mp="1a1mjge9vnu3n">{aggregates[1]?.label}</strong>.
          </p>
          <p data-id="element-2026" data-mp="3lmvjiiejj8uj">
            <strong data-id="element-2027" data-mp="3logcw4p4clqt">Key insight:</strong> Behaviour scores trail Characteristic
            scores in most domains — leaders are seen to hold the right
            qualities more consistently than they demonstrate them in practice.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2028" data-mp="3gwf4wuyzhh6l">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-2029" data-mp="32x709mk1xgv0">
          <div className="flex items-center" data-id="element-2030" data-mp="37vqq3y1rncsn">
            <User className="h-5 w-5 text-slate-500 mr-2" data-id="element-2031" data-mp="37xlb1cd1f4wq" />
            <h3 className="font-medium text-slate-900" data-id="element-2032" data-mp="2sz9c0z26zk2e">Individual Reports</h3>
          </div>
          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded-full font-medium" data-id="element-2033" data-mp="2xxkqjn3fmiqy">
            {individualCount} Ready
          </span>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-2034" data-mp="r5a2ai4pxart">
          {OVERVIEW_PEOPLE.map(({
          personId,
          score
        }) => {
          const p = personById(personId);
          return <button key={personId} onClick={onBrowseReports} className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-2035" data-mp="uqvd8ycboyk8">
                <div className="flex items-center" data-id="element-2036" data-mp="ksxq6e6797t7">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3" data-id="element-2037" data-mp="gquebofmc17e">
                    {p.initials}
                  </div>
                  <div data-id="element-2038" data-mp="18i07tm0u30np">
                    <div className="text-sm font-medium text-slate-900" data-id="element-2039" data-mp="18hgxaqdijejp">{p.name}</div>
                    <div className="text-xs text-slate-500" data-id="element-2040" data-mp="13ofj6kl4rj16">{p.title}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-2041" data-mp="zmapjluommej">
                  <div className="text-right" data-id="element-2042" data-mp="1w7b8l3i6ov1u">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-2043" data-mp="1w8475h6zoe8w">{score}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-2044" data-mp="1rdgjtlnq1pvw">
                      Avg Score
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400 -rotate-90" data-id="element-2045" data-mp="3f277kou54tam" />
                </div>
              </button>;
        })}
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50 text-center" data-id="element-2046" data-mp="3o1u4axbxdaxc">
          <button onClick={onBrowseReports} className="text-sm text-slate-600 font-medium hover:text-slate-900" data-id="element-2047" data-mp="3t16t3nmcws2k">
            View All {individualCount} Reports
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2048" data-mp="6mnf3s1anewm">
        <div className="p-5 border-b border-slate-200 flex items-center bg-slate-50" data-id="element-2049" data-mp="anfpwpptdrv0">
          <Users className="h-5 w-5 text-slate-500 mr-2" data-id="element-2050" data-mp="fmslpzyny99q" />
          <h3 className="font-medium text-slate-900" data-id="element-2051" data-mp="fk65lbxpilfw">Executive Aggregates</h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-2052" data-mp="kln2yxgzfprq">
          {aggregates.map(item => <div key={item.label} data-id="element-2053" data-mp="6xbu8f4u47ky">
              <div className="flex justify-between text-sm mb-1 gap-3" data-id="element-2054" data-mp="6waazndo0es5">
                <span className="font-medium text-slate-700" data-id="element-2055" data-mp="1z0ks5igbz39">{item.label}</span>
                <span className="text-slate-900 font-semibold" data-id="element-2056" data-mp="gn4w2of6d8c0">
                  {item.score.toFixed(1)}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-2057" data-mp="brpnaaw0y2mg">
                <div className={`h-2 rounded-full ${item.score >= 4 ? 'bg-emerald-500' : item.score >= 3.5 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{
              width: `${item.score / 5 * 100}%`
            }} data-id="element-2058" data-mp="3k5ezw7sgif38" />
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}
function ReportRow({
  report
}: {
  report: Report;
}) {
  const isExecutive = report.type === 'Executive Summary';
  const isAggregate = report.type === 'Board Aggregate';
  return <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-slate-50 transition-colors group" data-id="element-2059" data-mp="14g4yemc1c0si">
      <div className="flex items-start mb-4 sm:mb-0 min-w-0" data-id="element-2060" data-mp="1gwd2p0c14agt">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mr-4 ${isExecutive ? 'bg-indigo-50 text-indigo-600' : isAggregate ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`} data-id="element-2061" data-mp="1gujqz6mk701y">
          <FileText className="h-5 w-5" data-id="element-2062" data-mp="1unj79au60yak" />
        </div>
        <div className="min-w-0" data-id="element-2063" data-mp="1uq4b25e0xoay">
          <h4 className="text-sm font-semibold text-slate-900 line-clamp-1" data-id="element-2064" data-mp="1zmup6n69q9tq">
            {report.title}
          </h4>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-slate-500" data-id="element-2065" data-mp="1lwiviju1c30h">
            <span className={`px-2 py-0.5 rounded-full font-medium ${isExecutive ? 'bg-indigo-100 text-indigo-700' : isAggregate ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-2066" data-mp="1pwj9k3d82c70">
              {report.type}
            </span>
            <span data-id="element-2067" data-mp="2s9lzjraeug5v">{report.cycle}</span>
            <span className="text-slate-300" data-id="element-2068" data-mp="2s8kqttgva5rb">•</span>
            <span data-id="element-2069" data-mp="2o80topwan256">{report.date}</span>
            <span className="text-slate-300" data-id="element-2070" data-mp="2o7qymnwe64sm">•</span>
            <span data-id="element-2071" data-mp="2o667l89ad3zi">{report.size}</span>
          </div>
          {report.domains.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-2072" data-mp="2jaqw2myr3k69">
              {report.domains.map(d => <span key={d} className="px-2 py-0.5 rounded-full text-[11px] bg-white border border-slate-200 text-slate-600" data-id="element-2073" data-mp="2ec75uad4jm83">
                  {d}
                </span>)}
            </div>}
        </div>
      </div>

      <div className="flex items-center space-x-2 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity self-end sm:self-auto shrink-0" data-id="element-2074" data-mp="20g236yo9x07g">
        <button aria-label={`Share ${report.title}`} title="Share" className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors" data-id="element-2075" data-mp="1qjqpc491c2aw">
          <Share2 className="h-4 w-4" data-id="element-2076" data-mp="1bi0cna5u7798" />
        </button>
        <button aria-label={`Print ${report.title}`} title="Print" className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors" data-id="element-2077" data-mp="1bjbjjiukwv4k">
          <Printer className="h-4 w-4" data-id="element-2078" data-mp="1lgptr0pwsnof" />
        </button>
        <button aria-label={`Download ${report.title}`} title="Download" className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors" data-id="element-2079" data-mp="1qe7va61lk0me">
          <Download className="h-4 w-4" data-id="element-2080" data-mp="sowfheteuc9v" />
        </button>
        <button aria-label={`More options for ${report.title}`} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors hidden sm:block" data-id="element-2081" data-mp="xqlu14fud82p">
          <MoreVertical className="h-4 w-4" data-id="element-2082" data-mp="17fvk6er4kgo6" />
        </button>
      </div>
    </div>;
}